import LocalStorageManager from './localStorageManager'

class LocalStorageInterceptor {
  static registeredConfigs = []

  static register(configs) {
    if (!Array.isArray(configs)) {
      configs = [configs]
    }
    this.registeredConfigs.push(...configs)
  }

  static validate() {
    if (!this._canValidate()) {
      return true
    }

    return this.registeredConfigs.every(config => {
      if (this._shouldSkipValidation(config)) {
        return true
      }

      const isValid = this._validateStorages(config.storages)

      if (!isValid) {
        this._handleValidationFailure(config)
        return false
      }

      return true
    })
  }

  static _canValidate() {
    return LocalStorageManager.isAvailable() && this._isWindowAvailable()
  }

  static _isWindowAvailable() {
    return typeof window !== 'undefined' && window.location
  }

  static _shouldSkipValidation(config) {
    if (!config.publicPaths || config.publicPaths.length === 0) {
      return false
    }

    const currentPath = window.location.pathname

    return config.publicPaths.some(pathConfig => {
      if (typeof pathConfig === 'string') {
        if (pathConfig === '/') {
          return currentPath === '/'
        }
        return currentPath === pathConfig || currentPath.startsWith(pathConfig)
      }

      if (pathConfig.exact) {
        return currentPath === pathConfig.path
      }

      if (pathConfig.path === '/') {
        return currentPath === '/'
      }

      return currentPath.startsWith(pathConfig.path)
    })
  }

  static _validateStorages(storages) {
    return storages.every(storageConfig => {
      const data = this._getStorageData(storageConfig.key)
      return this._isValidStorageData(data, storageConfig)
    })
  }

  static _getStorageData(storageKey) {
    return LocalStorageManager.getJSON(storageKey, null)
  }

  static _isValidStorageData(data, config) {
    if (!data) {
      return false
    }

    if (config.tokenPath && !this._isValidField(data, config.tokenPath)) {
      return false
    }

    if (config.dataPath) {
      const nestedData = this._getNestedValue(data, config.dataPath)
      if (!nestedData) {
        return false
      }
      return this._hasRequiredFields(nestedData, config.requiredFields)
    }

    return this._hasRequiredFields(data, config.requiredFields)
  }

  static _getNestedValue(obj, path) {
    if (!path) return obj
    return path.split('.').reduce((current, key) => current?.[key], obj)
  }

  static _isValidField(data, fieldPath) {
    const value = this._getNestedValue(data, fieldPath)
    return value && this._isNonEmptyString(value)
  }

  static _hasRequiredFields(data, requiredFields) {
    if (!requiredFields || requiredFields.length === 0) {
      return true
    }

    return requiredFields.every(field => {
      const value = data[field]
      return (
        value !== null && value !== undefined && this._isNonEmptyString(value)
      )
    })
  }

  static _isNonEmptyString(value) {
    return value.toString().trim() !== ''
  }

  static _handleValidationFailure(config) {
    this._clearStorages(config.storages)

    if (config.onFailure) {
      if (typeof config.onFailure === 'function') {
        config.onFailure()
      } else if (config.onFailure.redirect) {
        this._redirect(config.onFailure.redirect)
      }
    }
  }

  static _clearStorages(storages) {
    storages.forEach(storageConfig => {
      LocalStorageManager.removeItem(storageConfig.key)

      if (storageConfig.additionalKeys) {
        storageConfig.additionalKeys.forEach(key => {
          LocalStorageManager.removeItem(key)
        })
      }
    })
  }

  static _redirect(url) {
    if (this._isWindowAvailable()) {
      window.location.href = url
    }
  }

  static init(configs) {
    if (!LocalStorageManager.isAvailable()) {
      return
    }

    if (configs) {
      this.register(configs)
    }

    this.validate()
  }

  static reset() {
    this.registeredConfigs = []
  }
}

export default LocalStorageInterceptor
